import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { SingSetup, SongPreview } from '~/interfaces';
import { Icon } from '~/modules/elements/akui/icon';
import SongPreviewLayout from '~/modules/elements/song-preview-layout';
import VideoPlayer, { VideoPlayerRef, VideoState } from '~/modules/elements/video-player/index';
import useDebounce from '~/modules/hooks/use-debounce';
import { isEurovisionSong } from '~/modules/songs/utils/special-songs-theme-checks';
import { FeatureFlags } from '~/modules/utils/feature-flags';
import useFeatureFlag from '~/modules/utils/use-feature-flag';
import { SongCard } from '~/routes/sing-a-song/song-selection/components/song-card';
import SongSettings from '~/routes/sing-a-song/song-selection/components/song-settings/index';
import { useSpecialTheme } from '~/routes/sing-a-song/song-selection/hooks/use-special-theme';

interface Props {
  songPreview: SongPreview;
  onPlay: (setup: SingSetup & { song: SongPreview }) => void;
  keyboardControl: boolean;
  onExitKeyboardControl: () => void;
  top: number;
  left: number;
  width: number;
  height: number;
  isPopular: boolean;
  forceFlag: boolean;
  onExpand: () => void;
  /** Swaps the open card over to another song — what the expanded card's series lane picks. */
  onSelectSong: (songId: string) => void;
}

const PREVIEW_LENGTH = 30;

export default function SongPreviewComponent({
  songPreview,
  top,
  left,
  width,
  height,
  keyboardControl,
  onExitKeyboardControl,
  onPlay,
  isPopular,
  forceFlag,
  onExpand,
  onSelectSong,
}: Props) {
  const [showVideo, setShowVideo] = useState(false);
  /**
   * Bumped every time the player finishes its API handshake, so the load below is issued again
   * once it is actually listening.
   *
   * Commands sent before that are posted to an iframe that is not listening yet and are dropped,
   * silently and for good - see `onReady` in `youtube.tsx`. Instrumenting the real app shows the
   * load going out *before* ready fires, so whether the preview starts is a race: it usually wins
   * on a fast machine, and when it loses there was previously nothing to try again, because
   * nothing this effect depends on changes afterwards.
   *
   * Counted rather than flagged: `useUnstuckYouTubePlayer` remounts the player when it decides it
   * has wedged, and the fresh iframe needs telling again just as much as the first one did.
   */
  const [playerReadyCount, setPlayerReadyCount] = useState(0);
  const player = useRef<VideoPlayerRef | null>(null);
  const thumbnailRef = useRef<HTMLDivElement | null>(null);
  const thumbnailSize = useRef<{ w: number; h: number } | null>(null);
  useSpecialTheme(songPreview, FeatureFlags.Eurovision, isEurovisionSong, 'eurovision');
  const newVolumeFFEnabled = useFeatureFlag(FeatureFlags.NewVolume);

  const expanded = keyboardControl;

  // Keep the YouTube iframe sized to the thumbnail element at all times.
  // The Thumbnail is now always the same DOM element so this only needs to run once.
  // The ResizeObserver fires whenever the thumbnail resizes (e.g. on expand/collapse).
  useEffect(() => {
    const el = thumbnailRef.current;
    if (!el) return;
    const applySize = (w: number, h: number) => {
      thumbnailSize.current = { w, h };
      player.current?.setSize(w, h);
    };
    const observer = new ResizeObserver(([entry]) => {
      const { width: w, height: h } = entry.contentRect;
      applySize(Math.round(w), Math.round(h));
    });
    observer.observe(el);
    const { width: w, height: h } = el.getBoundingClientRect();
    applySize(Math.round(w), Math.round(h));
    return () => observer.disconnect();
  }, []);

  const start = songPreview.previewStart ?? (songPreview.videoGap ?? 0) + 60;
  const end = songPreview.previewEnd ?? start + PREVIEW_LENGTH;
  const songPreviewVolume = newVolumeFFEnabled
    ? (songPreview.volume ?? songPreview.manualVolume)
    : songPreview.manualVolume;
  const undebounced = useMemo(
    () => [songPreview.video, start, end, songPreviewVolume] as const,
    [songPreview.video, start, end, songPreviewVolume],
  );
  const [videoId, previewStart, previewEnd, volume] = useDebounce(undebounced, 350);

  // Hide immediately whenever the selected song changes; the PLAYING event in
  // onVideoStateChange will reveal the video once the new one has actually loaded.
  useLayoutEffect(() => {
    setShowVideo(false);
  }, [songPreview.video]);

  useEffect(() => {
    // Re-apply size here because the YouTube IFrame API may not have been ready
    // when setSize was first called via ResizeObserver (getInternalPlayer() returns
    // null until the player script finishes loading). This effect fires after the
    // 350 ms debounce by which time the API is reliably initialised.
    if (thumbnailSize.current) {
      player.current?.setSize(thumbnailSize.current.w, thumbnailSize.current.h);
    }
    player.current?.loadVideoById({
      videoId: videoId,
      startSeconds: previewStart,
      endSeconds: previewEnd,
    });
    player.current?.playVideo();
  }, [videoId, player, previewStart, previewEnd, playerReadyCount]);

  const onVideoStateChange = useCallback(
    (state: VideoState) => {
      if (state === VideoState.ENDED) {
        // Reload (rather than seekTo) so the endSeconds bound is re-established — a bare seek
        // doesn't reliably re-arm it, and the preview would play past `previewEnd` on the next loop.
        player.current?.loadVideoById({ videoId, startSeconds: previewStart, endSeconds: previewEnd });
        player.current?.playVideo();
      } else if (state === VideoState.PLAYING) {
        setShowVideo(true);
      }
    },
    [videoId, previewStart, previewEnd],
  );

  const backButton = (
    <button
      onClick={onExitKeyboardControl}
      className="text-active flex items-center gap-1.5 transition-colors hover:opacity-80">
      <Icon icon="ic:baseline-arrow-back" className="text-lg" />
      <span className="text-lg font-bold">Sing a song</span>
    </button>
  );

  // Desktop: icon-only, inline with the title — no room/need for the "Sing a song" label there.
  const backIconButton = (
    <button onClick={onExitKeyboardControl} className="text-active hidden shrink-0 cursor-pointer items-center sm:flex">
      <Icon icon="ic:baseline-arrow-back" className="text-xl md:text-2xl" />
    </button>
  );

  const animationDurationSec =
    60 /
    (songPreview.realBpm && songPreview.realBpm > 40
      ? songPreview.realBpm
      : songPreview.bpm > 300
        ? songPreview.bpm / 4
        : songPreview.bpm / 2);

  return (
    <>
      {/* BPM rhythm shadow — sibling to SongCard so its scale animation isn't clipped by overflow-hidden */}
      {!expanded && showVideo && (
        <div
          className="pointer-events-none absolute z-2 hidden rounded-2xl bg-white opacity-0 blur-md md:block"
          style={{
            width,
            height,
            top,
            left,

            animationName: 'bpm',
            animationDelay: `${Math.ceil(0.3 / animationDurationSec) * animationDurationSec}s`, // sync to beat, starting on an even iteration
            animationDuration: `${animationDurationSec}s`,
            animationIterationCount: 'infinite',
            animationTimingFunction: 'ease-out',
          }}
        />
      )}

      {/* Backdrop — only shown when expanded */}
      {expanded && (
        <div
          className="fixed inset-0 z-200 bg-black/50 bg-[radial-gradient(transparent_3px,rgba(0,0,0,0.5)_3px)] bg-size-[10px_10px] backdrop-blur-[20px]"
          onClick={onExitKeyboardControl}
        />
      )}

      <SongCard
        song={songPreview}
        isPopular={isPopular}
        forceFlag={forceFlag}
        data-show-video={showVideo}
        data-expanded={expanded || undefined}
        data-song={songPreview.id}
        data-test="song-preview"
        focused={!expanded}
        className={
          'bg-slate-800 ' +
          (expanded
            ? // 1560px wide with 28px of padding, as the card is drawn, stopping 7rem short of the
              // viewport to clear the keyboard hint strip along the bottom. On a screen shorter than
              // 1080 it scrolls rather than clipping — the keyboard already brings what it focuses
              // into view, so nothing on it becomes unreachable.
              'fixed inset-0 z-202 [scrollbar-width:none] overflow-y-auto rounded-none p-3 sm:top-1/2 sm:right-auto sm:bottom-auto sm:left-1/2 sm:h-auto sm:max-h-[calc(100vh-7rem)] sm:w-[min(90vw,97.5rem)] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl sm:p-7 [&::-webkit-scrollbar]:hidden'
            : `absolute z-3 transition-opacity ${
                showVideo ? 'opacity-100 duration-300' : 'pointer-events-none opacity-0 duration-0'
              }`)
        }
        style={expanded ? {} : { width, height, top, left }}>
        {/* Click-capture overlay — collapsed only */}
        {!expanded && <div className="absolute inset-0 z-10 cursor-pointer" onClick={onExpand} />}

        <SongPreviewLayout
          expanded={expanded}
          variant="stacked"
          back={backButton}
          title={
            <div className="flex min-w-0 items-center gap-2 sm:gap-4">
              {/* Back button — icon-only, next to the title, desktop only, shown when expanded */}
              {backIconButton}
              <SongCard.SongTitle className="typography min-w-0 truncate text-xl! [view-transition-name:song-preview-title] sm:text-[2.125rem]! xl:text-[2.75rem]!" />
            </div>
          }
          artist={
            <SongCard.Artist className="typography text-md shrink-0 truncate font-bold text-white/70 [view-transition-name:song-preview-artist] sm:text-2xl xl:text-[1.75rem]" />
          }
          thumbnail={
            <SongCard.Thumbnail ref={thumbnailRef} className="w-full [view-transition-name:song-preview-thumbnail]">
              <div className={showVideo ? 'opacity-100 transition-opacity duration-500' : 'opacity-0'}>
                <VideoPlayer
                  width={0}
                  height={0}
                  disablekb
                  ref={player}
                  video={''}
                  volume={volume}
                  onReady={() => setPlayerReadyCount((count) => count + 1)}
                  onStateChange={onVideoStateChange}
                />
              </div>
              <SongCard.Thumbnail.Genre className={expanded ? 'sm:h-[2.125rem] sm:text-[1.0625rem]' : ''} />
              <SongCard.Thumbnail.Length className={expanded ? 'sm:h-[2.125rem] sm:text-[1.0625rem]' : ''} />
            </SongCard.Thumbnail>
          }
          collapsedFooter={
            <SongCard.Footer>
              <SongCard.SongTitle className="[view-transition-name:song-preview-title]" />
              <SongCard.Artist className="[view-transition-name:song-preview-artist]" />
              <SongCard.Badges>
                <SongCard.Badges.Flag />
                <SongCard.Badges.Duet />
                <SongCard.Badges.Stats focused compact />
              </SongCard.Badges>
            </SongCard.Footer>
          }
          footer={
            <SongSettings
              songPreview={songPreview}
              onPlay={onPlay}
              keyboardControl={keyboardControl}
              onExitKeyboardControl={onExitKeyboardControl}
              onSelectSong={onSelectSong}
            />
          }
        />
      </SongCard>
    </>
  );
}
